import {
  getFontCss,
  Popup,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import moment from 'moment-timezone'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectPeopleContactsRemovingFromState } from 'src/app/PeopleContacts/redux/peopleContacts.selectors'
import { CancelOrgInviteToastMessage } from 'src/app/Toast/Toast'
import { ButtonDesigns } from 'src/components/Button/Button'
import ConfirmationPopupContent from 'src/components/ConfirmationPopupContent/ConfirmationPopupContent'
import Trns from 'src/localization/Trns'
import { OrgInviteStatus } from 'src/models/people.models'
import { closeModal } from 'src/navigation/Lnk'
import { createSetIsCancelOrgInvitePopupOpen } from 'src/redux/actions/app.actions'
import { putOrganizationInvite } from 'src/redux/actions/data/organizations.actions'
import {
  selectCancelUserInviteFromRoute,
  selectInviteIdFromRoute,
  selectUserInviteFromRoute,
  selectUserInviteFromUserIdRoute,
} from 'src/redux/selectors/data/invitedToOrg.selectors'
import { css } from 'styled-components'

const CancelOrgInvitePopup: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch()

  const userInviteFromUserId = useSelector(selectUserInviteFromUserIdRoute)
  const userInviteFromCancelInviteId = useSelector(
    selectCancelUserInviteFromRoute,
  )
  const userInviteFromInviteId = useSelector(selectUserInviteFromRoute)
  const removing = useSelector(selectPeopleContactsRemovingFromState)
  const { analytics } = useAnalyticsContext()
  const inviteIdFromRoute = useSelector(selectInviteIdFromRoute)

  const invite =
    userInviteFromUserId ||
    userInviteFromInviteId ||
    userInviteFromCancelInviteId

  const { firstName, lastName, email, id, verified } = invite?.invitee || {}

  const userHasNoName = firstName === undefined && lastName === undefined

  const cancelInvite = (): void => {
    if (invite) {
      dispatch(
        putOrganizationInvite(
          OrgInviteStatus.Cancelled,
          invite,
          <CancelOrgInviteToastMessage organizationInvite={invite} />,
        ),
      )
      analytics?.track(
        'cancel org invite popup - confirm cancel invite - button press',
        {
          screen: `/user/${id}`,
          interaction: 'button press',
          intent: 'confirm cancel invite',
          verified: verified,
          createdAt: moment(invite?.createdAt).format('YYYY-MM-DD'),
        },
      )
      dispatch(createSetIsCancelOrgInvitePopupOpen(false))

      // close modal if we can get the invite id from the route (a.k.a. if we're on the invited screen)
      if (inviteIdFromRoute) {
        dispatch(closeModal())
      }
    }
  }

  const closePopup = (): void => {
    dispatch(createSetIsCancelOrgInvitePopupOpen(false))

    // close modal if we can get the invite id from the route (a.k.a. if we're on the invited screen)
    if (inviteIdFromRoute) {
      dispatch(closeModal())
    }
  }

  return (
    <Popup
      onClickClose={closePopup}
      showCloseButton
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <ConfirmationPopupContent
        title={<Trns source={'Confirm Invitation Cancellation'} />}
        subtitle={
          <Trns
            source={
              "Are you sure you want to cancel the invitation you sent to ${nameAndEmail}? This will have no affect on their presence in your contacts list, or any budgets or virtual cards you've created for them."
            }
            expressions={{
              nameAndEmail: (
                <span
                  css={css`
                    ${getFontCss(
                      16,
                      themeConstants.colors.font_primary,
                      styleConstants.fontFamilies.medium,
                    )};
                  `}
                >
                  {userHasNoName
                    ? `${email}`
                    : `${firstName} ${lastName} (${email})`}
                </span>
              ),
            }}
          />
        }
        actionButtonLabel={<Trns source={'Yes, cancel the invitation'} />}
        actionButtonOnClick={cancelInvite}
        actionButtonLoading={removing}
        actionButtonDesign={ButtonDesigns.designRed}
        cancelButtonLabel={<Trns source={'No, do not cancel the invitation'} />}
        cancelButtonOnClick={closePopup}
      />
    </Popup>
  )
}

export default CancelOrgInvitePopup
